import CustomLinkWithArrow from '@/_application/core/components/commons/custom-link-with-arrow';
import {Image} from '@/_application/core/components/ui/image';
import Link from '@/_application/core/components/ui/link';
import {Swiper, SwiperSlide} from '@/_application/core/components/ui/slider';
import {getImageAssetUrl} from '@/_application/core/utils/common';
import {getCategoryUrl} from '@/_application/modules/categories/models/url';
import {ProductCard} from '@/_application/modules/products/components/cards/product-card';
import {Product} from '@/_application/modules/products/model/product';
import ProductAttributesSwiper from '@/_application/pages/home/components/product-category-spotlight/product-attributes-swiper.mobile';
import ProductCategorySpotlightBrand from '@/_application/pages/home/components/product-category-spotlight/product-category-spotlight-brand.mobile';
import {quantitativeAttributes} from '@/framework/static/filter-attribute-code-value-type';
import {HomepageFeaturedBlock, HighlightBrands} from '__generated__/__types__';
import {useTranslation} from 'next-i18next';
import {useCallback, useMemo} from 'react';
import BannerImage from '@/_application/core/components/commons/banner/banner-image';
import {LazyLoadBlock} from '@/_application/core/components/commons/lazy-load-block';
import {getBannerUrlByPath} from '@/_application/helpers/cdn-image';

const Diaper = getImageAssetUrl('icons/diaper.png');
const MilkBottle = getImageAssetUrl('icons/milk-bottle.png');

interface ProductCategorySpotlightMobileProps {
    isDiaper: boolean;
    data: HomepageFeaturedBlock & {
        fetchedProducts?: Product[];
        fetchedHighlightProduct?: Product | null;
    };
    highlightBrands?: HighlightBrands | null;
}

const ProductCategorySpotlight = ({isDiaper, data, highlightBrands}: ProductCategorySpotlightMobileProps) => {
    const {t} = useTranslation('common');

    // Use pre-fetched products from server
    const products = useMemo(() => data?.fetchedProducts ?? [], [data?.fetchedProducts]);
    const highlightProduct = data?.fetchedHighlightProduct ?? null;

    const bannerImages = useMemo(() => {
        return (data?.mobile_banner_images ?? []).filter(banner => banner !== null);
    }, [data?.mobile_banner_images]);

    const canonicalUrl = useMemo(() => {
        return data?.category_items?.[0]?.canonical_url ?? '';
    }, [data?.category_items]);

    const brandOptions = useMemo(() => {
        return data?.highlight_attribute?.options?.filter(option => option !== null) ?? [];
    }, [data?.highlight_attribute?.options]);

    // Check type of attribute to show on the left or the right side
    const firstAttributeCode = data?.product_attributes?.at(0)?.attribute_code ?? '';
    const isQuantitativeAttribute = useMemo(() => {
        return quantitativeAttributes.includes(firstAttributeCode);
    }, [firstAttributeCode]);

    const quantitativeAttributeIndex = isQuantitativeAttribute ? 0 : 1;
    const descriptiveAttributeIndex = isQuantitativeAttribute ? 1 : 0;

    const renderBannerImages = useCallback(() => {
        const validBannerImage = bannerImages?.filter(banner => banner?.image !== null);
        return (
            <Swiper slidesPerView={1.1} spaceBetween={16}>
                {validBannerImage.map((item, index) => (
                    <SwiperSlide key={`${index}-${item?.url_banner}`}>
                        <BannerImage
                            className="h-[140px] w-full rounded-lg"
                            imageUrl={getBannerUrlByPath(item?.image as string)}
                            bannerUrl={item?.url_banner}
                            width={330}
                            height={140}
                            alt="Banner"
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
        );
    }, [bannerImages]);

    const renderProductList = useCallback(() => {
        return products.slice(0, 8).map(item => (
            <LazyLoadBlock key={item?.sku} isLazyLoad={true}>
                <ProductCard product={item} key={item?.sku} />
            </LazyLoadBlock>
        ));
    }, [products]);

    const renderHighlightProductCard = useCallback(() => {
        if (!highlightProduct) {
            return null;
        }

        return (
            <LazyLoadBlock isLazyLoad={true}>
                <ProductCard
                    product={highlightProduct}
                    extraText={data?.highlight_item?.label ?? 'No label'}
                    extraIcon={data?.highlight_item?.icon ?? 'No icon'}
                    variant={'horizontal'}
                    size={'large'}
                />
            </LazyLoadBlock>
        );
    }, [highlightProduct, data?.highlight_item?.label, data?.highlight_item?.icon]);

    if (data?.category_items?.length === 0) {
        return null;
    }

    return (
        <LazyLoadBlock isLazyLoad={true}>
            <div data-testid="product-category-spotlight-mobile" className="mb-4 bg-white px-4 pb-5 pt-5">
                <div className="mb-3 flex items-center gap-2">
                    {isDiaper ? (
                        <Image src={Diaper} alt={data?.name ?? 'diaper-icon'} height={32} width={32} />
                    ) : (
                        <Image src={MilkBottle} alt={data?.name ?? 'milk-bottle-icon'} height={31} width={22} />
                    )}

                    <Link
                        href={data?.category_items?.[0] ? getCategoryUrl(data?.category_items?.[0]) : '/'}
                        className="text-sm font-extrabold text-KPZ_Blue-Dark transition-colors hover:text-KPZ_Orange-30 md:text-base">
                        <h2 data-testid="category-name-test-id">{data?.category_items?.[0]?.name ?? 'No name'}</h2>
                    </Link>
                </div>

                <div className="mb-5 border-b border-KPZ_divider pb-5">{renderBannerImages()}</div>

                {data?.product_attributes && data?.product_attributes?.length > 0 && (
                    <div className="mb-2.5 min-h-[94px] lg:min-h-[45px] grid grid-cols-1 gap-5 lg:grid-cols-2">
                        <div>
                            <ProductAttributesSwiper
                                data={data}
                                attributeIndex={descriptiveAttributeIndex}
                                isQuantitativeAttribute={false}
                                canonicalUrl={canonicalUrl}
                            />
                        </div>

                        <div>
                            <ProductAttributesSwiper
                                data={data}
                                attributeIndex={quantitativeAttributeIndex}
                                isQuantitativeAttribute={true}
                                canonicalUrl={canonicalUrl}
                            />
                        </div>
                    </div>
                )}

                {brandOptions.length > 0 && (
                    <ProductCategorySpotlightBrand options={brandOptions} highlightBrands={highlightBrands} />
                )}

                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    <div className="col-span-2">{renderHighlightProductCard()}</div>
                    {renderProductList()}
                </div>

                <div className="mt-5 flex w-full items-center justify-center">
                    <CustomLinkWithArrow href={canonicalUrl} text={t('text-view-all', 'Xem tất cả')} />
                </div>
            </div>
        </LazyLoadBlock>
    );
};

export default ProductCategorySpotlight;
